import { useResendEscriptSignupInvite } from '@/hooks/auth/useResendEscriptSignupInvite';
import { usePreOnboardingContext } from '@/hooks/escript/usePreOnboardingContext';
import { useResendInviteCountdown } from '@/hooks/escript/useResendInviteCountdown';
import { emitUIEvent } from '@montugroup/data-collection';
import { Link, toast } from '@montugroup/design-system';
import { styled } from '@mui/material/styles';

const RESEND_INVITE_COOLDOWN_SECONDS = 10;

type StyledLinkProps = {
  disabled: boolean;
};

const StyledLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'disabled' })<StyledLinkProps>(
  ({ disabled }) => ({
    cursor: 'pointer',
    ...(!disabled && {
      '&&': {
        // workaround to increase specificity instead of using '!important'
        textDecoration: 'underline',
      },
    }),
  }),
);

export const ResendInviteButton = () => {
  const { secondsRemaining, startCountdown } = useResendInviteCountdown(RESEND_INVITE_COOLDOWN_SECONDS);
  const { customerDetails, setAlert } = usePreOnboardingContext();
  const { mutate, isPending: isResendPending } = useResendEscriptSignupInvite();

  if (!customerDetails) {
    return null;
  }

  // todo: emitUIEvent is an interim approach until the data collection package is updated with 'Data Events' schema
  const handleResendClick = () => {
    startCountdown();
    mutate(
      { body: { data: { id: customerDetails.preOnboardUserId } } },
      {
        onSuccess: () => {
          setAlert({
            show: true,
            severity: 'success',
            title: '',
            content: 'Invite link sent.',
          });
          emitUIEvent(
            {
              component_name: 'resend_invite_button',
              event_name: 'success',
              id: 'escript_onboarding_token_success',
              value: 'Invite link sent success',
            },
            [],
          );
        },
        onError: () => {
          toast.warn('There was an error. Please try again soon.');
          emitUIEvent(
            {
              component_name: 'resend_invite_button',
              event_name: 'error',
              id: 'escript_onboarding_token_error',
              value: 'Invite link sent error',
            },
            [],
          );
        },
      },
    );
  };

  if (secondsRemaining > 0) {
    return <>Resend in {secondsRemaining}</>;
  }

  return (
    <StyledLink
      disabled={isResendPending}
      onClick={handleResendClick}
      data-tracking-id="escript_onboarding_resend_invite"
    >
      Resend invite
    </StyledLink>
  );
};
